import { useCallback, useEffect, useState } from 'react';
import AuthService from '../../../../shared/services/AuthService';
import { isMarketplaceBuyerRole } from '../../../../routes/roles';
import MarketplaceService from '../services/MarketplaceService';
import MarketplaceBuyerAuthService from '../services/MarketplaceBuyerAuthService';
import {
  addGuestWishlistSlug,
  getGuestWishlistSlugs,
  isGuestWishlisted,
  removeGuestWishlistSlug,
} from '../services/guestWishlistStore';

export function useWishlist(slug) {
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const isBuyer = isMarketplaceBuyerRole(AuthService.getLoggedInRoleId());

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!slug) {
        setLoading(false);
        return;
      }
      if (isBuyer && AuthService.getToken()) {
        try {
          const favourites = await MarketplaceBuyerAuthService.getFavourites();
          if (!cancelled) {
            setSaved((favourites || []).some((f) => f.slug === slug && f.isAvailable !== false));
          }
        } catch {
          if (!cancelled) setSaved(false);
        }
      } else if (!cancelled) {
        setSaved(isGuestWishlisted(slug));
      }
      if (!cancelled) setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, [slug, isBuyer]);

  const toggle = useCallback(async () => {
    if (!slug) return { ok: false, requiresSignIn: false };

    if (isBuyer && AuthService.getToken()) {
      try {
        if (saved) {
          await MarketplaceService.removeFavourite(slug);
          setSaved(false);
        } else {
          await MarketplaceService.addFavourite(slug);
          setSaved(true);
        }
        return { ok: true, requiresSignIn: false };
      } catch (err) {
        return { ok: false, error: err?.response?.data?.message || 'Unable to update wishlist.' };
      }
    }

    if (saved) {
      removeGuestWishlistSlug(slug);
      setSaved(false);
    } else {
      addGuestWishlistSlug(slug);
      setSaved(true);
    }
    return { ok: true, requiresSignIn: false, guest: true };
  }, [slug, saved, isBuyer]);

  return { saved, loading, toggle, isBuyer, guestCount: getGuestWishlistSlugs().length };
}

export function useGuestWishlistCount() {
  const [count, setCount] = useState(getGuestWishlistSlugs().length);
  useEffect(() => {
    const refresh = () => setCount(getGuestWishlistSlugs().length);
    window.addEventListener('storage', refresh);
    window.addEventListener('nf-guest-wishlist-changed', refresh);
    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener('nf-guest-wishlist-changed', refresh);
    };
  }, []);
  return count;
}

export function notifyGuestWishlistChanged() {
  window.dispatchEvent(new Event('nf-guest-wishlist-changed'));
}
